import { useTranslate } from '@refinedev/core';
import { Table, Tooltip, Image, Checkbox } from 'antd';
import type { ScalarDiffFieldOut } from '@/api/generated/schemas';
import { diffFieldLabel } from './diffFieldLabel';
import { hasOverridableFields, willImportApply } from './diffFieldApply';
import { ExclamationCircleFilled, LockFilled } from '@ant-design/icons';
import dayjs from 'dayjs';

interface HeaderRow {
  key: string;
  isHeader: true;
  sectionName: string;
  path?: undefined;
  local?: undefined;
  upstream?: undefined;
  type?: undefined;
}

interface DataRow extends ScalarDiffFieldOut {
  key: string;
  isHeader?: false;
  sectionName?: undefined;
}

type DiffRow = HeaderRow | DataRow;

interface ImportDiffTableProps {
  fields: ScalarDiffFieldOut[];
  /** Import will also overwrite manually edited values. */
  overrideManual: boolean;
  onOverrideManualChange: (checked: boolean) => void;
}

/**
 * The local-vs-upstream diff of one publication: a sectioned table plus the
 * "also overwrite manually edited values" checkbox when it applies. The
 * caller owns fetching the diff and applying the import.
 */
export const ImportDiffTable = ({
  fields,
  overrideManual,
  onOverrideManualChange,
}: ImportDiffTableProps) => {
  const t = useTranslate();

  // Group diff fields into sections
  const general = fields.filter(
    (f) => !f.path.startsWith('ebook.') && !f.path.startsWith('audio.')
  );
  const ebook = fields.filter((f) => f.path.startsWith('ebook.'));
  const audio = fields.filter((f) => f.path.startsWith('audio.'));

  const dataSource: DiffRow[] = [];
  if (general.length > 0) {
    dataSource.push({
      key: 'g-header',
      isHeader: true,
      sectionName: t('user_catalog.publications.import.sections.general'),
    });
    dataSource.push(...general.map((f) => ({ ...f, key: f.path })));
  }
  if (ebook.length > 0) {
    dataSource.push({
      key: 'e-header',
      isHeader: true,
      sectionName: t('user_catalog.publications.import.sections.ebook'),
    });
    dataSource.push(...ebook.map((f) => ({ ...f, key: f.path })));
  }
  if (audio.length > 0) {
    dataSource.push({
      key: 'a-header',
      isHeader: true,
      sectionName: t('user_catalog.publications.import.sections.audio'),
    });
    dataSource.push(...audio.map((f) => ({ ...f, key: f.path })));
  }

  const renderCoverUrl = (value: unknown) => {
    if (value === null || value === undefined) {
      return <span style={{ color: '#bfbfbf' }}>—</span>;
    }
    const srcStr = String(value);
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          alignItems: 'flex-start',
        }}
      >
        <Image
          src={srcStr}
          width={100}
          style={{
            borderRadius: '4px',
            border: '1px solid #f0f0f0',
            maxWidth: '100%',
          }}
        />
        <span
          style={{ fontSize: '11px', color: '#8c8c8c', wordBreak: 'break-all' }}
        >
          {srcStr}
        </span>
      </div>
    );
  };

  const renderIdentifier = (value: unknown) => {
    if (value === null || value === undefined) {
      return <span style={{ color: '#bfbfbf' }}>—</span>;
    }
    return <span style={{ fontFamily: 'monospace' }}>{String(value)}</span>;
  };

  const customRenderers: Record<string, (val: unknown) => React.ReactNode> = {
    cover_url: renderCoverUrl,
    'ebook.isbn': renderIdentifier,
    'audio.isbn': renderIdentifier,
    'ebook.ean': renderIdentifier,
    'audio.ean': renderIdentifier,
    'ebook.published_at': (val) =>
      dayjs(val as string).isValid()
        ? dayjs(val as string).format('YYYY-MM-DD HH:mm:ss')
        : String(val),
    'ebook.withdrawal_at': (val) =>
      dayjs(val as string).isValid()
        ? dayjs(val as string).format('YYYY-MM-DD HH:mm:ss')
        : String(val),
    'ebook.available_from': (val) =>
      dayjs(val as string).isValid()
        ? dayjs(val as string).format('YYYY-MM-DD HH:mm:ss')
        : String(val),
    'ebook.available_till': (val) =>
      dayjs(val as string).isValid()
        ? dayjs(val as string).format('YYYY-MM-DD HH:mm:ss')
        : String(val),
    'audio.published_at': (val) =>
      dayjs(val as string).isValid()
        ? dayjs(val as string).format('YYYY-MM-DD HH:mm:ss')
        : String(val),
    'audio.withdrawal_at': (val) =>
      dayjs(val as string).isValid()
        ? dayjs(val as string).format('YYYY-MM-DD HH:mm:ss')
        : String(val),
    'audio.available_from': (val) =>
      dayjs(val as string).isValid()
        ? dayjs(val as string).format('YYYY-MM-DD HH:mm:ss')
        : String(val),
    'audio.available_till': (val) =>
      dayjs(val as string).isValid()
        ? dayjs(val as string).format('YYYY-MM-DD HH:mm:ss')
        : String(val),
  };

  const renderValue = (value: unknown, path: string) => {
    if (value === null || value === undefined) {
      return <span style={{ color: '#bfbfbf' }}>—</span>;
    }

    if (customRenderers[path]) {
      return customRenderers[path](value);
    }

    if (typeof value === 'object') {
      const stringified = JSON.stringify(value);
      if (stringified.length > 60) {
        return (
          <Tooltip title={stringified}>
            <span>{stringified.substring(0, 60)}...</span>
          </Tooltip>
        );
      }
      return stringified;
    }

    const strValue = String(value);
    if (strValue.length > 60) {
      return (
        <Tooltip title={strValue}>
          <span>{strValue.substring(0, 60)}...</span>
        </Tooltip>
      );
    }

    return strValue;
  };

  const getFieldLabel = (path: string): string => diffFieldLabel(t, path);

  // Why the upstream value is struck through, if it is: a lock beats the
  // manual-edit reason because the override cannot lift it.
  const skipReason = (record: DataRow): 'locked' | 'manual' | null => {
    if (record.import_locked) return 'locked';
    if (!willImportApply(record, overrideManual)) return 'manual';
    return null;
  };

  const renderValueCell = (
    value: unknown,
    path: string,
    isUpstream: boolean,
    skipped: 'locked' | 'manual' | null = null
  ) => {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          alignItems: 'flex-start',
        }}
      >
        <span
          style={{
            fontSize: '9.5px',
            letterSpacing: '0.6px',
            fontWeight: 700,
            color: isUpstream ? '#0f7a4d' : '#8c8c8c',
            textTransform: 'uppercase',
          }}
        >
          {isUpstream
            ? t('user_catalog.publications.import.value_origin.upstream')
            : t('user_catalog.publications.import.value_origin.local')}
        </span>
        <div
          style={{
            marginTop: '2px',
            width: '100%',
            opacity: skipped ? 0.45 : undefined,
            textDecoration: skipped ? 'line-through' : undefined,
          }}
        >
          {renderValue(value, path)}
        </div>
        {skipped && (
          <span style={{ fontSize: '11px', fontWeight: 600, color: '#faad14' }}>
            {t(
              skipped === 'locked'
                ? 'user_catalog.publications.import.import_locked_skip'
                : 'user_catalog.publications.import.manually_edited_skip'
            )}
          </span>
        )}
      </div>
    );
  };

  const columns = [
    {
      title: t('user_catalog.publications.import.columns.field'),
      dataIndex: 'path',
      key: 'path',
      width: '35%',
      render: (text: string, record: DiffRow) => {
        if (record.isHeader) {
          return {
            children: (
              <span style={{ fontWeight: 600, color: '#1890ff' }}>
                {record.sectionName}
              </span>
            ),
            props: { colSpan: 3 },
          };
        }
        const label = getFieldLabel(text);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span
              style={{ fontWeight: 500, fontSize: '13px', color: '#262626' }}
            >
              {label}
              {record.import_locked ? (
                <Tooltip
                  title={t(
                    'user_catalog.publications.import.import_locked_tooltip'
                  )}
                >
                  <LockFilled
                    style={{
                      marginLeft: 6,
                      fontSize: '13px',
                      color: '#8c8c8c',
                    }}
                  />
                </Tooltip>
              ) : null}
              {!record.import_locked &&
                record.manually_edited &&
                !overrideManual && (
                  <Tooltip
                    title={t(
                      'user_catalog.publications.import.manually_edited_tooltip'
                    )}
                  >
                    <ExclamationCircleFilled
                      style={{
                        marginLeft: 6,
                        fontSize: '13px',
                        color: '#faad14',
                      }}
                    />
                  </Tooltip>
                )}
            </span>
            <span
              style={{
                fontSize: '11px',
                color: '#8c8c8c',
                fontFamily: 'monospace',
              }}
            >
              {text}
            </span>
          </div>
        );
      },
    },
    {
      title: t('user_catalog.publications.import.columns.local'),
      dataIndex: 'local',
      key: 'local',
      width: '32%',
      render: (value: unknown, record: DiffRow) => {
        if (record.isHeader) {
          return { props: { colSpan: 0 } };
        }
        return renderValueCell(value, record.path ?? '', false);
      },
    },
    {
      title: t('user_catalog.publications.import.columns.upstream'),
      dataIndex: 'upstream',
      key: 'upstream',
      width: '32%',
      render: (value: unknown, record: DiffRow) => {
        if (record.isHeader) {
          return { props: { colSpan: 0 } };
        }
        return renderValueCell(
          value,
          record.path ?? '',
          true,
          skipReason(record)
        );
      },
    },
  ];

  // A locked manual edit does not count: the override cannot lift the lock,
  // so offering the checkbox would promise a write that never happens.
  const hasManuallyEdited = hasOverridableFields(fields);

  return (
    <>
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        size="small"
        bordered
      />
      {hasManuallyEdited && (
        <Checkbox
          checked={overrideManual}
          onChange={(e) => onOverrideManualChange(e.target.checked)}
          style={{ marginTop: 16 }}
        >
          {t('user_catalog.publications.import.override_manual_label')}
        </Checkbox>
      )}
    </>
  );
};
